import { useRoute, type RouteProp } from '@react-navigation/native';
import Country from 'country-state-city/lib/country';
import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

import { yupResolver } from '@hookform/resolvers/yup';

import { showAlert, showConfirm } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { auth } from '@/features/auth/types';
import { useGuardianPlayerDetailQuery } from '@/features/guardian-player/queries';
import {
  useCreateGuardianPlayerMutation,
  useDeleteGuardianPlayerMutation,
  useGuardianProfileQuery,
  useUpdateGettingStartedMutation,
  useUpdateGuardianPlayerMutation,
} from '@/features/guardian/queries';
import {
  INITIAL_GUARDIAN_ADD_PLAYER_FORM,
  guardianAddPlayerSchema,
  type DominantHand,
  type GuardianAddPlayerForm,
} from '@/features/guardian/schemas/guardian-add-player-schema';
import type { CreateGuardianPlayerRequest } from '@/features/guardian/services/create-guardian-player';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { WELCOME_STEPS } from '@/features/guardian/services/save-welcome-progress';
import {
  formatHeightInchesDisplay,
  fromDateOnly,
  toDateOnly,
} from '@/features/onboarding/components/onboarding-add-player-sheet/form-helpers';
import { useGuardianStackNavigation } from '@/hooks';
import { useAvatarPicker } from '@/hooks/use-avatar-picker';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import type { GuardianStackParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { normalizePlayerGender } from '@/utils/player-gender';

const strings = STRINGS.onboarding.addPlayer;

/** Country name → ISO code (the Country field stores the ISO code). */
function resolveCountryCode(countryName?: string | null): string | null {
  if (!countryName) return null;
  return Country.getAllCountries().find(c => c.name === countryName)?.isoCode ?? null;
}

/** Prefill the form from an existing player (edit mode, list row). The saved address keeps
 *  the country as its display name, so resolve it back to the ISO code the Country field
 *  expects (which also drives whether State shows the picker or a free-text field). */
function playerToAddForm(player: GuardianPlayer): GuardianAddPlayerForm {
  return {
    ...INITIAL_GUARDIAN_ADD_PLAYER_FORM,
    firstName: player.firstname ?? '',
    lastName: player.lastname ?? '',
    dob: fromDateOnly(player.dob?.slice(0, 10)),
    gender: normalizePlayerGender(player.gender),
    height: player.height ? formatHeightInchesDisplay(String(player.height)) : '',
    dominantHand: (player.dominantHand as DominantHand) || 'right',
    country: resolveCountryCode(player.address?.country),
    state: player.address?.state ?? null,
    city: player.address?.city ?? '',
    zipCode: player.address?.zipcode ?? '',
    avatar: player.avatarUrl ? { uri: player.avatarUrl } : null,
  };
}

/** Prefill the form from the fetched player detail (edit mode). The detail shape differs from
 *  the list row — `dob` may be a Firestore timestamp, `dominanthand` is a boolean, and the
 *  avatar arrives under `avatar_url`/`photo_url`/`image_url`. */
function detailToAddForm(detail: auth.GuardianPlayerDetail): GuardianAddPlayerForm {
  let dobStr: string | undefined;
  if (detail.dob && typeof detail.dob === 'object' && 'seconds' in detail.dob) {
    dobStr = new Date(detail.dob.seconds * 1000).toISOString();
  } else if (typeof detail.dob === 'string') {
    dobStr = detail.dob;
  }
  const avatarUri = detail.avatar_url ?? detail.photo_url ?? detail.image_url;
  return {
    ...INITIAL_GUARDIAN_ADD_PLAYER_FORM,
    firstName: detail.firstname ?? '',
    lastName: detail.lastname ?? '',
    dob: fromDateOnly(dobStr?.slice(0, 10)),
    gender: normalizePlayerGender(detail.gender),
    height: detail.height ? formatHeightInchesDisplay(String(detail.height)) : '',
    // `dominanthand` boolean: true = left, false = right (see player/utils/helpers).
    dominantHand: detail.dominanthand ? 'left' : 'right',
    country: resolveCountryCode(detail.address?.country),
    state: detail.address?.state ?? null,
    city: detail.address?.city ?? '',
    zipCode: detail.address?.zipcode ?? '',
    avatar: avatarUri ? { uri: avatarUri } : null,
  };
}

/**
 * View-model for the Add/Edit Player screen (PP-376). Owns the form, the create/update
 * mutations, avatar upload, and the address/same-address logic. `mode: 'edit'` (with a
 * `player` route param) prefills and PATCHes; otherwise it POSTs a new player. Height /
 * Need clubs / Dominant hand aren't in the design but are required by the API — sent as
 * safe defaults on create and carried through unchanged on edit.
 */
export function useAddPlayer() {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<RouteProp<GuardianStackParamList, 'AddPlayer'>>();
  const editingPlayer = route.params?.mode === 'edit' ? route.params.player : undefined;
  const isEdit = Boolean(editingPlayer);

  const uid = useSessionStore(s => s.user?.uid);
  const { data: guardian } = useGuardianProfileQuery(uid);
  const { mutateAsync: createPlayer } = useCreateGuardianPlayerMutation(uid);
  const { mutate: markWelcomeStep } = useUpdateGettingStartedMutation(uid);
  const { mutateAsync: updatePlayer } = useUpdateGuardianPlayerMutation(uid);
  const { mutateAsync: deletePlayer } = useDeleteGuardianPlayerMutation(uid);
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const pickAvatar = useAvatarPicker();

  const { control, handleSubmit, setValue, reset } = useForm<GuardianAddPlayerForm>({
    resolver: yupResolver(guardianAddPlayerSchema) as never,
    defaultValues: editingPlayer
      ? playerToAddForm(editingPlayer)
      : INITIAL_GUARDIAN_ADD_PLAYER_FORM,
    mode: 'onChange',
  });

  // Edit mode: fetch the full player detail and re-seed the form once (the route param is only
  // a list row and may be stale/incomplete). Hydrate a single time so it doesn't clobber edits.
  const { data: playerDetail } = useGuardianPlayerDetailQuery(editingPlayer?.id ?? '');
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (!playerDetail || didHydrateRef.current) return;
    didHydrateRef.current = true;
    // Detail arrives async; keep anything the user already edited (e.g. a just-ticked
    // "Same address") so a late hydrate can't wipe their changes before Save. The checkbox
    // itself always starts unchecked — the flag isn't stored server-side, and deriving it from
    // address equality mis-ticks players whose address merely happens to match the guardian's.
    reset(detailToAddForm(playerDetail), { keepDirtyValues: true });
  }, [playerDetail, reset]);

  const sameAddress = useWatch({ control, name: 'sameAddress' });
  const country = useWatch({ control, name: 'country' });

  const handlePickAvatar = () => {
    pickAvatar(value => {
      setValue('avatar', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    });
  };

  // "Same address with me" copies the guardian's address into the player's form fields.
  // The guardian stores the country name; the form field expects the ISO code.
  const handleToggleSameAddress = (checked: boolean) => {
    const dirty = { shouldDirty: true };
    setValue('sameAddress', checked, dirty);
    setValue('country', checked ? resolveCountryCode(guardian?.address?.country) : null, dirty);
    setValue('state', checked ? (guardian?.address?.state ?? null) : null, dirty);
    setValue('city', checked ? (guardian?.address?.city ?? '') : '', dirty);
    setValue('zipCode', checked ? (guardian?.address?.zipcode ?? '') : '', dirty);
  };

  // Changing country resets the State/Province value (its input mode / list changes).
  const handleChangeCountry = (next: string) => {
    setValue('country', next, { shouldDirty: true });
    setValue('state', null, { shouldDirty: true });
  };

  // Edit only — confirm, delete the player, then return to the previous screen.
  const handleDelete = async () => {
    if (!editingPlayer) return;
    const confirmed = await showConfirm({
      title: strings.deleteConfirmTitle,
      message: strings.deleteConfirmMessage,
      confirmText: strings.deleteConfirm,
      cancelText: strings.deleteCancel,
      confirmStyle: 'destructive',
    });
    if (!confirmed) return;
    try {
      showGlobalLoading();
      await deletePlayer(editingPlayer.id);
      // Edit is opened from the Player Detail screen, so pop past both this form AND that detail
      // — otherwise `goBack()` lands on the now-deleted player's (stale) detail screen.
      navigation.pop(2);
    } catch {
      showAlert({ title: strings.deleteErrorTitle, message: strings.deleteErrorMessage });
    } finally {
      hideGlobalLoading();
    }
  };

  const onSubmit = handleSubmit(async values => {
    const gender = values.gender;
    if (!gender) return;

    try {
      showGlobalLoading();

      const avatarUri = values.avatar?.uri;
      let avatarUrl: string | undefined;
      if (uid && shouldUploadAvatarUri(avatarUri)) {
        const resp = await uploadImageToStorage(
          avatarUri,
          `profile-pictures/player/${uid}-${Date.now()}.jpg`,
        );
        avatarUrl = `${resp.downloadUrl}&v=${Date.now()}`;
      } else if (avatarUri) {
        avatarUrl = avatarUri;
      }

      // The "Same address" toggle already copied the guardian's address into the form fields,
      // so build the payload from the form uniformly. Country holds the ISO code on the form;
      // the API stores the country name. Send every sub-field (as `null` when empty) so a PATCH
      // overwrites the old value instead of keeping it — otherwise omitted fields stay stale
      // (e.g. copying a guardian whose city/state/zip are null would leave the player's old ones).
      const countryName = values.country
        ? (Country.getCountryByCode(values.country)?.name ?? null)
        : null;
      const hasAddress = Boolean(countryName || values.city || values.state || values.zipCode);
      const address: NonNullable<CreateGuardianPlayerRequest['address']> | undefined = hasAddress
        ? {
            country: countryName,
            state: values.state || null,
            city: values.city || null,
            zipcode: values.zipCode || null,
          }
        : undefined;

      const payload = {
        firstname: values.firstName.trim(),
        lastname: values.lastName.trim(),
        // Local calendar day (YYYY-MM-DD); toISOString() would shift east-of-UTC users back a day.
        dob: toDateOnly(values.dob!),
        gender,
        // Height + dominant hand are editable in edit mode; on create they keep the form
        // defaults. `needClubs` isn't in the design at all — default it / carry it through.
        needClubs: (editingPlayer?.needClubs ?? 'no') as 'yes' | 'no',
        dominantHand: values.dominantHand,
        height: values.height ? Number(values.height.replace(/"/g, '')) : undefined,
        address,
        avatarUrl,
      };

      if (editingPlayer) {
        await updatePlayer({ playerId: editingPlayer.id, data: payload });
      } else {
        await createPlayer(payload);
        // Adding the FIRST player completes Get Started step 1. Only mark it if it isn't already
        // done — re-marking a finished step returns 409 Conflict from the server.
        if (
          guardian?.gettingStarted?.steps?.[String(WELCOME_STEPS.ADD_PLAYER)] !== 'done' &&
          guardian?.gettingStarted?.status !== 'completed'
        ) {
          markWelcomeStep(WELCOME_STEPS.ADD_PLAYER);
        }
      }
      navigation.goBack();
    } catch {
      // The shared server-error interceptor already surfaces the failure; stay on the screen.
    } finally {
      hideGlobalLoading();
    }
  });

  return {
    control,
    isEdit,
    screenTitle: isEdit ? strings.editScreenTitle : strings.screenTitle,
    sameAddress,
    country,
    onBack: navigation.goBack,
    onCancel: navigation.goBack,
    onPickAvatar: handlePickAvatar,
    onToggleSameAddress: handleToggleSameAddress,
    onChangeCountry: handleChangeCountry,
    onDelete: handleDelete,
    onSubmit,
  };
}
